import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationsApi,
  CreateNotificationDto,
  PreviewNotificationDto,
  NotificationType,
  AudienceType,
} from '../../api/notifications';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Loader2, Eye, Send, Calendar, Users, TrendingUp, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/shared/ConfirmDialog';

interface CreateNotificationPageProps {
  isDark: boolean;
}

export default function CreateNotificationPage({ isDark }: CreateNotificationPageProps) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [sendNow, setSendNow] = useState(true);
  const [formData, setFormData] = useState<CreateNotificationDto>({
    title: '',
    body: '',
    type: NotificationType.SYSTEM,
    data: {},
    audience_filters: { audience_type: AudienceType.ALL },
    priority: 5,
    is_sponsored: false,
    is_premium: false,
  });
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const handlePreview = async () => {
    if (!formData.title || !formData.body) {
      toast.error('Заполните заголовок и текст');
      return;
    }
    setIsPreviewLoading(true);
    try {
      const previewDto: PreviewNotificationDto = {
        title: formData.title,
        body: formData.body,
        type: formData.type,
        data: formData.data,
        audience_filters: formData.audience_filters,
      };
      const data = await notificationsApi.preview(previewDto);
      setPreviewData(data);
      toast.success('Предпросмотр рассчитан');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось рассчитать предпросмотр');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.body) {
      toast.error('Заполните заголовок и текст');
      return;
    }

    let scheduledAt: string | undefined = undefined;
    if (!sendNow) {
      if (!scheduledDate || !scheduledTime) {
        toast.error('Укажите дату и время отправки');
        return;
      }
      const localDate = new Date(`${scheduledDate}T${scheduledTime}:00`);
      if (localDate.getTime() <= Date.now()) {
        toast.error('Время отправки должно быть в будущем');
        return;
      }
      scheduledAt = localDate.toISOString();
    }

    // Подтверждение для массовой рассылки всем пользователям.
    if (formData.audience_filters?.audience_type === AudienceType.ALL) {
      const recipients = previewData?.estimated_recipients;
      const pushRecipients = previewData?.estimated_push_recipients;
      const ok = await confirm({
        title: sendNow ? 'Отправить уведомление всем?' : 'Запланировать рассылку всем?',
        description: recipients
          ? `Сообщение увидят ~${recipients.toLocaleString('ru-RU')} чел., push уйдёт ~${(pushRecipients ?? 0).toLocaleString('ru-RU')} чел. Действие нельзя отменить после отправки.`
          : 'Уведомление получат все пользователи. Действие нельзя отменить после отправки.',
        confirmLabel: sendNow ? 'Отправить' : 'Запланировать',
      });
      if (!ok) return;
    }

    setIsLoading(true);
    try {
      const submitData: CreateNotificationDto = { ...formData, scheduled_at: scheduledAt };
      await notificationsApi.create(submitData);
      toast.success(sendNow ? 'Уведомление отправлено' : 'Уведомление запланировано');
      navigate('/notifications');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось создать уведомление');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/notifications')} className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : '')}>
          <ArrowLeft className="w-4 h-4 mr-2" />Назад
        </Button>
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>Создать уведомление</h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Отправка push-уведомлений</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>Детали уведомления</h2>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Заголовок *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Заголовок уведомления"
                  className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
              </div>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Текст *</Label>
                <Textarea value={formData.body} onChange={(e) => setFormData({ ...formData, body: e.target.value })} placeholder="Текст уведомления" rows={4}
                  className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
              </div>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Тип</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value as NotificationType })}>
                  <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NotificationType.SYSTEM}>Система</SelectItem>
                    <SelectItem value={NotificationType.PAYMENT}>Платёж</SelectItem>
                    <SelectItem value={NotificationType.SUBSCRIPTION}>Подписка</SelectItem>
                    <SelectItem value={NotificationType.PROMO}>Промо</SelectItem>
                    <SelectItem value={NotificationType.REMINDER}>Напоминание</SelectItem>
                    <SelectItem value={NotificationType.ACHIEVEMENT}>Достижение</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Deep Link (необязательно)</Label>
                <Input value={formData.data?.deep_link || ''} onChange={(e) => setFormData({ ...formData, data: { ...formData.data, deep_link: e.target.value } })} placeholder="imtihonplus://Payment"
                  className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
              </div>
            </div>
          </Card>

          {/* Audience */}
          <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>Аудитория</h2>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Тип аудитории</Label>
                <Select value={formData.audience_filters?.audience_type || AudienceType.ALL} onValueChange={(value) => setFormData({ ...formData, audience_filters: { ...formData.audience_filters, audience_type: value as AudienceType } })}>
                  <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={AudienceType.ALL}>Все</SelectItem>
                    <SelectItem value={AudienceType.PREMIUM}>Премиум</SelectItem>
                    <SelectItem value={AudienceType.FREE}>Бесплатные</SelectItem>
                    <SelectItem value={AudienceType.BY_PREFERENCES}>По предпочтениям</SelectItem>
                    <SelectItem value={AudienceType.BY_ACTIVITY}>По активности</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.audience_filters?.audience_type === AudienceType.BY_ACTIVITY && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Дней активности</Label>
                    <Input type="number" value={formData.audience_filters?.activity_filters?.days_active || ''} placeholder="30"
                      onChange={(e) => setFormData({ ...formData, audience_filters: { ...formData.audience_filters!, activity_filters: { ...formData.audience_filters?.activity_filters, days_active: e.target.value ? parseInt(e.target.value) : undefined } } })}
                      className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
                  </div>
                  <div className="space-y-2">
                    <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Дней неактивности</Label>
                    <Input type="number" value={formData.audience_filters?.activity_filters?.days_inactive || ''} placeholder="30"
                      onChange={(e) => setFormData({ ...formData, audience_filters: { ...formData.audience_filters!, activity_filters: { ...formData.audience_filters?.activity_filters, days_inactive: e.target.value ? parseInt(e.target.value) : undefined } } })}
                      className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Schedule */}
          <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>Расписание</h2>
                <div className="flex items-center gap-2">
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Отправить сейчас</Label>
                  <Switch checked={sendNow} onCheckedChange={setSendNow} />
                </div>
              </div>
              {!sendNow && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Дата</Label>
                    <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
                  </div>
                  <div className="space-y-2">
                    <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Время</Label>
                    <Input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Options */}
          <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>Опции</h2>
              <div className="flex items-center justify-between">
                <div>
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Рекламное уведомление</Label>
                  <p className={cn('text-xs mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Пометить как рекламу</p>
                </div>
                <Switch checked={formData.is_sponsored} onCheckedChange={(checked) => setFormData({ ...formData, is_sponsored: checked })} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Премиум-уведомление</Label>
                  <p className={cn('text-xs mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Пометить как премиум</p>
                </div>
                <Switch checked={formData.is_premium} onCheckedChange={(checked) => setFormData({ ...formData, is_premium: checked })} />
              </div>
              <div className="space-y-2">
                <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Приоритет (1-10)</Label>
                <Input type="number" min="1" max="10" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 5 })}
                  className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <Button onClick={handlePreview} disabled={isPreviewLoading} variant="outline" className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
              {isPreviewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}Предпросмотр
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading} className="bg-[#C9A227] hover:bg-[#C9A227]/90 flex-1">
              {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : sendNow ? <Send className="w-4 h-4 mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
              {sendNow ? 'Отправить сейчас' : 'Запланировать'}
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-1">
          <Card className={cn('sticky top-24', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <div className="p-6 space-y-4">
              <h2 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>Предпросмотр</h2>
              {previewData ? (
                <div className="space-y-4">
                  <div className={cn('p-4 rounded-lg', isDark ? 'bg-slate-700' : 'bg-slate-50')}>
                    <div className={cn('font-semibold mb-1', isDark ? 'text-white' : 'text-[#111111]')}>{formData.title}</div>
                    <div className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>{formData.body}</div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><Users className={cn('w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} /><span className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>Увидят сообщение</span></div>
                      <span className={cn('font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>{previewData.estimated_recipients.toLocaleString('ru-RU')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><Bell className={cn('w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} /><span className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>Получат push</span></div>
                      <span className={cn('font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>{(previewData.estimated_push_recipients ?? 0).toLocaleString('ru-RU')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><TrendingUp className={cn('w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} /><span className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>Устройства</span></div>
                      <span className={cn('font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>{previewData.estimated_devices.toLocaleString('ru-RU')}</span>
                    </div>
                  </div>

                  <p className={cn('text-xs leading-relaxed', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    Push уходит только тем, кто разрешил уведомления. Остальные увидят
                    сообщение в приложении — это нормально, что числа отличаются.
                  </p>
                  <div className={cn('p-3 rounded-lg text-sm', isDark ? 'bg-slate-700' : 'bg-slate-50')}>
                    <p className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{previewData.message}</p>
                  </div>
                </div>
              ) : (
                <div className={cn('text-center py-8 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>Click "Preview" to see estimated recipients</div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
